/**
 * CustomersCounts memoized Selectors
 *
 */

import { createSelector } from '@reduxjs/toolkit';

import {
  getSelectedPeriod,
  getSelectedStoresIds,
  getSelectedTimeAggregate,
} from '../../Preferences';
import { selectColorMapping, selectStores } from '../../Stores';
import { createChartData } from '../helpers';

/**
 * Get data about customers counts retrieved from api
 *
 * @return {Object}
 */
export const getCustomersCountsEntities = ({ entities }) => entities.customersCounts.entities;

/**
 * Get status about customers counts request
 * `none`, `loading`, `loaded` or `failed`
 *
 * @return {string}
 */
export const getCustomersCountsStatus = ({ entities }) => entities.customersCounts.status;

/**
 * If last attempt at fetching data resulted in an error, returns the error value
 *
 * @return {string}
 */
export const getCustomersCountsError = ({ entities }) => entities.customersCounts.error;

/**
 * If data is being fetched
 *
 * @return {boolean}
 */
export const areCustomersCountsLoading = createSelector(
  [getCustomersCountsStatus],
  status => status === 'loading',
);

/**
 *
 * Get customers counts data of selected stores
 *
 * @return {Object}
 */
export const getSelectedCustomersCountsChart = createSelector(
  [getCustomersCountsEntities, getSelectedStoresIds],
  (customersCountsEntities, selectedStoreIds) => {
    if (!customersCountsEntities) {
      return null;
    }
    const customersCounts = Object.keys(customersCountsEntities).reduce(
      (dataOfSelectedStores, storeId) =>
        selectedStoreIds.includes(parseInt(storeId, 10))
          ? {
              ...dataOfSelectedStores,
              ...(customersCountsEntities[storeId]
                ? { [storeId]: customersCountsEntities[storeId] }
                : {}),
            }
          : dataOfSelectedStores,
      {},
    );
    if (!Object.keys(customersCounts).length) {
      return null;
    }

    return customersCounts;
  },
);

/**
 * Calculates customers counts for bar chart on selected stores
 *
 * @return {Array}
 */
export const getCustomersCountsChartData = createSelector(
  [
    getSelectedPeriod,
    getSelectedTimeAggregate,
    getSelectedCustomersCountsChart,
    selectStores,
    selectColorMapping,
  ],
  (period, aggregate, customersCounts, stores, colorMapping) =>
    customersCounts
      ? createChartData({
          interval: period.interval,
          data: customersCounts,
          aggregate,
          colorMapping,
          stores,
        })
      : [],
);
